import React from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import './FilterPanel.css';

export { FilterControl, FilterInputGroup, FilterSelect } from './FilterControls';
export { default as FilterAssigneeCombobox } from './FilterAssigneeCombobox';
export type { AssigneePeerOption } from './FilterAssigneeCombobox';
export { default as FilterDropdownSelect } from './FilterDropdownSelect';
export { default as FilterMultiSelect, formatMultiSelectLabel } from './FilterMultiSelect';
export type { FilterSelectOption } from './filterDropdownShared';

export interface ActiveFilter {
  id: string;
  label: string;
  /** Quando informado, o chip vira removível e limpa apenas este critério. */
  onRemove?: () => void;
}

const slugify = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

interface FilterChipProps {
  filter: ActiveFilter;
}

const FilterChip: React.FC<FilterChipProps> = ({ filter }) => {
  if (!filter.onRemove) {
    return (
      <span className="filter-chip" title={filter.label}>
        <span className="filter-chip__label">{filter.label}</span>
      </span>
    );
  }

  return (
    <span className="filter-chip filter-chip--removable" title={filter.label}>
      <span className="filter-chip__label">{filter.label}</span>
      <button
        type="button"
        className="filter-chip__remove"
        aria-label={`Remover filtro: ${filter.label}`}
        onClick={filter.onRemove}
      >
        <span aria-hidden="true">×</span>
      </button>
    </span>
  );
};

interface FilterPanelProps {
  /** Id do corpo colapsável, referenciado pelo aria-controls do botão. */
  panelId: string;
  activeFilters: ActiveFilter[];
  expanded: boolean;
  onToggleExpanded: () => void;
  onClearAll?: () => void;
  title?: string;
  /** Mensagem de contexto sobre o resultado (ex.: itens ocultos pela regra de conclusão). */
  notice?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  panelId,
  activeFilters,
  expanded,
  onToggleExpanded,
  onClearAll,
  title = 'Filtros',
  notice,
  className,
  children,
}) => {
  const activeCount = activeFilters.length;

  return (
    <Card
      className={`filter-panel dashboard-filters-card dashboard-section-card csp-wave1-overflow-visible mb-3${
        className ? ` ${className}` : ''
      }`}
    >
      <Card.Header className="filter-panel__header">
        <div className="filter-panel__title">
          <i className="bi bi-funnel" aria-hidden="true" />
          <span>{title}</span>
          {activeCount > 0 ? (
            <span className="filter-panel__count">
              {activeCount}
              <span className="visually-hidden">
                {activeCount === 1 ? ' filtro aplicado' : ' filtros aplicados'}
              </span>
            </span>
          ) : (
            <span className="filter-panel__empty">Sem filtros ativos</span>
          )}
        </div>
        <div className="filter-panel__actions">
          <Button
            variant="outline-secondary"
            size="sm"
            className="dashboard-filter-action-btn dashboard-filter-action-btn--toggle"
            aria-expanded={expanded}
            aria-controls={panelId}
            onClick={onToggleExpanded}
          >
            <i className={`bi ${expanded ? 'bi-chevron-up' : 'bi-chevron-down'} me-1`} aria-hidden="true" />
            {expanded ? 'Ocultar filtros' : 'Mostrar filtros'}
          </Button>
          {onClearAll ? (
            <Button
              variant="outline-secondary"
              size="sm"
              className="dashboard-filter-action-btn dashboard-filter-action-btn--clear"
              disabled={activeCount === 0}
              onClick={onClearAll}
            >
              Limpar tudo
            </Button>
          ) : null}
        </div>
      </Card.Header>

      {activeCount > 0 ? (
        <div className="filter-panel__chips" role="group" aria-label="Filtros aplicados">
          {activeFilters.map((filter) => (
            <FilterChip key={filter.id} filter={filter} />
          ))}
        </div>
      ) : null}

      {notice ? (
        <div className="filter-panel__notice" role="status">
          <i className="bi bi-info-circle me-2" aria-hidden="true" />
          <span>{notice}</span>
        </div>
      ) : null}

      <Card.Body id={panelId} className="filter-panel__body" hidden={!expanded}>
        {children}
      </Card.Body>
    </Card>
  );
};

interface FilterSectionProps {
  title: string;
  /** `inline` empilha os controles lado a lado (usado para os interruptores de exibição). */
  layout?: 'grid' | 'inline';
  children: React.ReactNode;
}

export const FilterSection: React.FC<FilterSectionProps> = ({ title, layout = 'grid', children }) => {
  const headingId = `filter-section-${slugify(title)}`;

  return (
    <section className="filter-panel__section" role="group" aria-labelledby={headingId}>
      <h3 className="filter-panel__section-title" id={headingId}>
        {title}
      </h3>
      <div className={layout === 'inline' ? 'filter-panel__toggles' : 'filter-panel__grid'}>{children}</div>
    </section>
  );
};

interface FilterFieldProps {
  label: string;
  controlId: string;
  hint?: React.ReactNode;
  /** Ocupa duas colunas do grid (campos de busca, por exemplo). */
  wide?: boolean;
  children: React.ReactNode;
}

export const FilterField: React.FC<FilterFieldProps> = ({ label, controlId, hint, wide, children }) => (
  <div className={`filter-panel__field${wide ? ' filter-panel__field--wide' : ''}`}>
    <Form.Label htmlFor={controlId} className="filter-panel__label dashboard-filter-label">
      {label}
    </Form.Label>
    {children}
    {hint ? (
      <p className="filter-panel__hint" id={`${controlId}-help`}>
        {hint}
      </p>
    ) : null}
  </div>
);

interface FilterToggleProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export const FilterToggle: React.FC<FilterToggleProps> = ({ id, label, checked, onChange }) => (
  <div className={`filter-panel__toggle${checked ? ' filter-panel__toggle--on' : ''}`}>
    <Form.Check
      type="switch"
      id={id}
      label={label}
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
    />
  </div>
);

export default FilterPanel;
